import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { createDialog } from '@/components/create-dialog'
import type { DialogActionValue, DialogComponentProps } from '@/components/create-dialog'
import { Label, RadioGroup, RadioGroupItem } from '@/components/ui'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { TTipoClienteProposta } from '@/features/proposta/entities/Comprador/validator/excluir-comprador-validator'
import { tipoCompradorKeyMap } from '@/features/proposta/utils/key-map'

interface DialogExcluirCompradorOptions {
  clientes: Array<{
    id: string
    nome: string
    tipo: TTipoClienteProposta
  }>
}

interface DialogExcluirCompradorResult {
  clienteSelecionadoId: string
}

const DialogExcluirCompradorComponent = ({
  onAction,
  options: { clientes },
}: DialogComponentProps<DialogExcluirCompradorOptions, DialogExcluirCompradorResult>) => {
  const { t } = useTranslation('proposta')
  const [open, setOpen] = useState(true)
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState<string>('')

  const isCompradorComVinculos = clientes.length > 1
  const dialogTitle = isCompradorComVinculos
    ? t('cadastro_compradores.dialog_excluir_comprador.titulo_plural')
    : t('cadastro_compradores.dialog_excluir_comprador.titulo_singular')
  const disabledExcluir = isCompradorComVinculos && !clienteSelecionadoId

  const handleAction = (value: DialogActionValue) => {
    setOpen(false)
    if (value !== 'confirm') {
      onAction(value)
      return
    }

    onAction(value, { clienteSelecionadoId: isCompradorComVinculos ? clienteSelecionadoId : clientes[0]!.id })
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) onAction('dismiss')
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="gap-6">
        <AlertDialogHeader className="space-y-2">
          <AlertDialogTitle className="text-xl tracking-wider">{dialogTitle}</AlertDialogTitle>
          <AlertDialogDescription className="font-nunito text-neutral-600">
            {t('cadastro_compradores.dialog_excluir_comprador.descricao')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {isCompradorComVinculos && (
          <RadioGroup
            className="-mx-6 border-t border-b border-neutral-100 px-6 py-4"
            value={clienteSelecionadoId}
            onValueChange={setClienteSelecionadoId}
          >
            {clientes.map(({ id, nome, tipo }) => (
              <div className="flex items-center gap-2" key={id}>
                <RadioGroupItem value={id} id={id} />
                <Label
                  className="font-nunito text-base font-normal tracking-wide text-neutral-700"
                  htmlFor={id}
                >{`${nome} (${tipoCompradorKeyMap[tipo]})`}</Label>
              </div>
            ))}
          </RadioGroup>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handleAction('cancel')}>
            {t('cadastro_compradores.dialog_excluir_comprador.voltar')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => handleAction('confirm')} disabled={disabledExcluir}>
            {t('cadastro_compradores.dialog_excluir_comprador.excluir')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const DialogExcluirComprador = createDialog(DialogExcluirCompradorComponent)
